import { Component, computed, inject, input, output, signal, ViewEncapsulation } from '@angular/core'
import { Dialog } from 'primeng/dialog'
import { TranslatePipe } from '@ngx-translate/core'
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental'
import { lastValueFrom } from 'rxjs'
import { AgreementTemplateService, AppUserAgreementRequestDto, AppUserAgreementService, AppUserService } from 'api'
import { ProgressSpinner } from 'primeng/progressspinner'
import { Button } from 'primeng/button'
import { InputOtp } from 'primeng/inputotp'
import { FormsModule } from '@angular/forms'
import { Checkbox } from 'primeng/checkbox'
import { ConfirmationService, MessageService } from 'primeng/api'
import Keycloak from 'keycloak-js'
import { ConfirmDialog } from 'primeng/confirmdialog'

@Component({
	selector: 'app-accept-agreement-modal',
	encapsulation: ViewEncapsulation.None,
	imports: [Dialog, TranslatePipe, ProgressSpinner, Button, InputOtp, FormsModule, Checkbox, ConfirmDialog],
	template: `
		<p-dialog
			[visible]="visible()"
			[modal]="true"
			[breakpoints]="{ '1199px': '75vw', '575px': '90vw' }"
			[style]="{ width: '40vw', height: '40vh' }"
			[draggable]="false"
			[resizable]="false"
			[closable]="this.unassignedAgreement.data()?.agreementType !== 'SIGNATURE_REQUIRED'"
			[closeOnEscape]="false"
			[focusOnShow]="true"
			[maximizable]="true"
			[header]="('agreement.details.title' | translate) + '	' + unassignedAgreement.data()?.title"
			(visibleChange)="onClose()">
			<div class="flex flex-col items-center gap-2 w-full h-full max-w-4xl">
				@switch (unassignedAgreement.status()) {
					@case ('pending') {
						<div class="flex justify-center items-center h-64">
							<p-progress-spinner
								[style]="{ width: '50px', height: '50px' }"
								strokeWidth="8"
								fill="transparent"
								animationDuration=".5s" />
						</div>
					}
					@case ('error') {}
					@default {
						<div class="w-full flex justify-center items-center mt-3">
							<div class="p-2 w-full">
								<!--								<h2 class="text-2xl font-bold mb-4 text-gray-800 flex items-center gap-2">-->
								<!--									<span class="i-heroicons-document-text-solid text-blue-500 text-3xl"></span>-->
								<!--									{{ 'agreementTemplates.agreementTypes.' + unassignedAgreement.data()?.agreementType | translate }}-->
								<!--								</h2>-->
								<div class="mb-2 flex flex-col gap-1">
									<div class="flex justify-between items-center">
										<!--										<label for="title" class="font-medium text-gray-600">{{-->
										<!--											'agreement.details.status' | translate-->
										<!--										}}</label>-->
										<!--										<span id="title" class="text-gray-900">{{ unassignedAgreement.data()?.title }}</span>-->
									</div>
									<div class="flex justify-between items-center">
										<div
											[innerHTML]="unassignedAgreement.data()?.content || '-'"
											class="inner-font px-5 py-4 my-5 break-words text-xs leading-relaxed max-w-full overflow-auto"
											id="content"></div>
									</div>
									<div class="flex gap-2 my-3 items-center">
										<p-checkbox [(ngModel)]="accepted" [disabled]="shouldShowOTP()" [binary]="true" />
										تمامی بندهای قرارداد و مفاد آن را مطالعه نموده ام و میپذیرم.
									</div>
									@if (shouldShowOTP()) {
										<div class="flex justify-start my-4 items-center">
											<p-input-otp
												[(ngModel)]="otpValue"
												[disabled]="acceptUserAgreement.isPending() || getAccount.isPending()"
												[integerOnly]="true"
												[length]="6"
												(ngModelChange)="otpValue().length === 6 && accept($event)"
												id="otp"
												dir="ltr" />
										</div>
									}
									@if (acceptUserAgreement.isPending() || getAccount.isPending()) {
										<div class="flex justify-center items-center h-64">
											<p-progress-spinner
												[style]="{ width: '25px', height: '25px' }"
												strokeWidth="8"
												fill="transparent"
												animationDuration=".5s" />
										</div>
									}

									<div class="flex justify-between items-center">
										<p-button
											[disabled]="
												!accepted() ||
												(shouldShowOTP() && !otpValue()) ||
												acceptUserAgreement.isPending() ||
												getAccount.isPending()
											"
											(onClick)="accept($event)"
											>{{ 'submit' | translate }}
										</p-button>
									</div>
								</div>
							</div>
						</div>
					}
				}
			</div>
			<p-confirm-dialog />
		</p-dialog>
	`,
	styles: `
		.inner-font h2 {
			font-size: 14px !important;
		}
	`,
})
export default class AcceptAgreementModal {
	visible = input.required<boolean>()
	unsignedAgreementId = input<string | null>(null)
	closeModal = output()
	protected readonly keycloakService = inject(Keycloak)

	#agreementTemplate = inject(AgreementTemplateService)
	#userAgreement = inject(AppUserAgreementService)
	#messageService = inject(MessageService)
	#confirmationService = inject(ConfirmationService)
	#appUserService = inject(AppUserService)

	syncAppUser = injectQuery(() => ({
		enabled: false,
		queryKey: ['sync-app'],
	}))
	getAccount = injectQuery(() => ({
		enabled: !!this.syncAppUser.data(),
		queryKey: ['get-account', this.syncAppUser.data()],
	}))

	unassignedAgreement = injectQuery(() => ({
		enabled: !!this.unsignedAgreementId(),
		queryKey: ['unassigned-agreement', this.unsignedAgreementId()],
		queryFn: () => lastValueFrom(this.#agreementTemplate.getByIdAgreementTemplate({ id: this.unsignedAgreementId()! })),
	}))

	acceptUserAgreement = injectMutation(() => ({
		mutationFn: (params: AppUserAgreementRequestDto) =>
			lastValueFrom(this.#userAgreement.acceptAgreement({ appUserAgreementRequestDto: params })),
		onSuccess: res => {
			if (res?.status === 'OTP_REQUIRED') {
				this.#messageService.add({
					detail: 'کد تایید ارسال شد!',
					key: 'tc',
					severity: 'success',
				})
				return
			}
			this.getAccount.refetch()
			if (res.agreementDetails?.id) {
				this.onClose()
			}
		},
	}))
	shouldShowOTP = computed(
		() =>
			this.unassignedAgreement.data()?.agreementType === 'SIGNATURE_REQUIRED' &&
			this.acceptUserAgreement.data()?.status === 'OTP_REQUIRED'
	)
	otpValue = signal('')
	accepted = signal(false)

	accept(event: Event) {
		if (this.unassignedAgreement.data()!.agreementType === 'SIMPLE_ACCEPTANCE') {
			this.acceptUserAgreement.mutate({ agreementTemplateId: this.unsignedAgreementId()! })
			return
		}
		if (!this.shouldShowOTP()) {
			this.#confirmationService.confirm({
				target: event.target as EventTarget,
				message: 'آیا مایل به ارسال کد  تایید به شماره تلفن ثبت شده در سایت هستید؟',
				header: 'هشدار',
				closable: true,
				closeOnEscape: true,
				icon: 'pi pi-exclamation-triangle',
				rejectButtonProps: {
					label: 'رد',
					severity: 'secondary',
					outlined: true,
				},
				acceptButtonProps: {
					label: 'تایید',
				},
				accept: () => {
					this.acceptUserAgreement.mutate({
						agreementTemplateId: this.unsignedAgreementId()!,
					})
				},
			})

			return
		}
		if (!this.otpValue()) {
			return
		}
		this.acceptUserAgreement.mutate({
			agreementTemplateId: this.unsignedAgreementId()!,
			otpCode: this.otpValue(),
		})
	}

	onClose() {
		this.otpValue.set('')
		this.accepted.set(false)
		this.closeModal.emit()
	}
}
